import type { ReactNode } from 'react'
import type { NavItem } from './sidebar-constants'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { LogOut, Menu, PanelLeftClose } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { queryClient } from '@/app/providers/query'
import { useTranslationJobsStore } from '@/entities/localized-speech/store/translation-jobs-store'
import { useLogoutMutation } from '@/entities/session'
import { STORAGE_KEYS } from '@/shared/constants'
import { DASHBOARD_LANGUAGE_OPTIONS } from '@/shared/constants/languages'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useDashboardDisplayLanguage } from '@/shared/hooks/use-dashboard-display-language'
import {
  buildScopedAdminPath,
  getAdminModuleRouteFromPath,
  getAdminScopeFromPath,
  getLoginPathForScope,
} from '@/shared/libs/admin-routes'
import { cn } from '@/shared/libs/cn'
import { useAuthStore } from '@/shared/store/auth-store'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'
import { NAV_SECTIONS, SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from './sidebar-constants'

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

interface NavItemButtonProps {
  item: NavItem
  label: string
  isSelected: boolean
  isCollapsed: boolean
  onClick: () => void
}

function NavItemButton({ item, label, isSelected, isCollapsed, onClick }: NavItemButtonProps) {
  const Icon = item.icon
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={cn(
        'flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm transition-colors',
        isSelected
          ? 'bg-blue-50 text-blue-600 font-medium border-l-2 border-blue-600'
          : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900',
        isCollapsed && 'justify-center px-2',
      )}
    >
      <Icon size={16} className="shrink-0" />
      {!isCollapsed && <span className="truncate">{label}</span>}
    </button>
  )
}

export interface AdminLayoutProps {
  children: ReactNode
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)
  const navigate = useNavigate()
  const currentPath = useRouterState({ select: (state) => state.location.pathname })
  const { t, i18n } = useTranslation()
  const { logout, user, isSuperAdmin, isTenantAdmin } = useAdminScope()
  const { activeLanguage, setActiveLanguage } = useDashboardDisplayLanguage()
  const logoutMutation = useLogoutMutation()
  const clearAllJobs = useTranslationJobsStore((s) => s.clearAllJobs)

  const currentScope = getAdminScopeFromPath(currentPath)
  const currentModulePath = getAdminModuleRouteFromPath(currentPath)
  const visibleNavSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => (!item.superAdminOnly || isSuperAdmin) && (!item.tenantAdminOnly || isTenantAdmin),
    ),
  })).filter((section) => section.items.length > 0)
  const flatNavItems = visibleNavSections.flatMap((section) => section.items)

  function hasRouteSegment(path: string, segment: string) {
    return (
      path === segment ||
      path.startsWith(`${segment}/`) ||
      path.includes(`/${segment}/`) ||
      path.endsWith(`/${segment}`)
    )
  }

  function isSurveyResponsesPath() {
    return (
      currentModulePath === '/surveys/responses' ||
      currentModulePath.endsWith('/surveys/responses') ||
      currentModulePath.includes('/surveys/responses/')
    )
  }

  function isActiveNavItem(route: string) {
    if (route === '/tenant') {
      return currentModulePath === '/tenant' || /^\/tenant\/[^/]+$/.test(currentModulePath)
    }
    if (route === '/tourist-areas') {
      return hasRouteSegment(currentModulePath, 'tourist-areas')
    }
    if (route === '/pois') {
      return hasRouteSegment(currentModulePath, 'pois')
    }
    if (route === '/tours') {
      return hasRouteSegment(currentModulePath, 'tours')
    }
    if (route === '/ticket-configs') {
      return hasRouteSegment(currentModulePath, 'ticket-configs')
    }
    if (route === '/surveys/responses') {
      return isSurveyResponsesPath()
    }
    if (route === '/surveys') {
      return hasRouteSegment(currentModulePath, 'surveys') && !isSurveyResponsesPath()
    }
    return currentModulePath === route || currentModulePath.startsWith(`${route}/`)
  }

  const activeItem = flatNavItems
    .filter((item) => isActiveNavItem(item.route))
    .sort((a, b) => b.route.length - a.route.length)[0]

  function handleNavClick(item: NavItem) {
    const targetRoute = currentScope ? buildScopedAdminPath(currentScope, item.route) : item.route
    void navigate({ href: targetRoute })
  }

  async function handleLogout() {
    setLogoutDialogOpen(false)
    try {
      await logoutMutation.mutateAsync()
    } finally {
      clearAllJobs()
      void queryClient.cancelQueries()
      queryClient.clear()
      logout()
      void navigate({ href: getLoginPathForScope(currentScope) })
    }
  }

  function handleLanguageChange(language: (typeof DASHBOARD_LANGUAGE_OPTIONS)[number]) {
    setActiveLanguage(language.value)
    useAuthStore.getState().setLanguageCode(language.value)
    localStorage.setItem(STORAGE_KEYS.LANGUAGE, language.locale)
    void i18n.changeLanguage(language.locale)
    void queryClient.invalidateQueries()
  }

  return (
    <div className="flex h-screen box-border overflow-hidden bg-zinc-50 p-2 gap-2">
      {/* Sidebar */}
      <aside
        className="h-full bg-white border-r border-zinc-200 rounded-xl flex flex-col transition-all duration-300 ease-in-out shrink-0"
        style={{
          width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
          boxShadow: '0 1px 2px -1px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        {/* Brand + toggle */}
        <div
          className={cn(
            'flex items-center gap-2 px-3 py-3 border-b border-zinc-100',
            isCollapsed && 'justify-center gap-1 px-2 py-2',
          )}
        >
          <div className="w-6 h-6 rounded bg-blue-600 shrink-0" />
          {!isCollapsed && (
            <span className="flex-1 text-sm font-semibold text-zinc-900 truncate">
              {t('layout.adminPanel')}
            </span>
          )}
          <button
            type="button"
            onClick={() => setIsCollapsed((prev) => !prev)}
            title={isCollapsed ? t('layout.expandSidebar') : t('layout.collapseSidebar')}
            className="p-1 rounded-md hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600 transition-colors shrink-0"
          >
            {isCollapsed ? <Menu size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        {/* Nav sections */}
        <div className="flex-1 min-h-0 overflow-y-auto py-2 px-1 space-y-4">
          {visibleNavSections.map((section) => (
            <div key={section.labelKey}>
              {!isCollapsed && (
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest px-3 py-1">
                  {t(section.labelKey)}
                </p>
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavItemButton
                    key={item.key}
                    item={item}
                    label={t(item.labelKey)}
                    isSelected={activeItem?.key === item.key}
                    isCollapsed={isCollapsed}
                    onClick={() => handleNavClick(item)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* User section */}
        <div
          className={cn(
            'border-t border-zinc-100 flex',
            isCollapsed
              ? 'flex-col items-center justify-center gap-2 p-2'
              : 'items-center gap-3 p-3',
          )}
        >
          <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold shrink-0">
            {getInitials(user?.name ?? t('layout.fallbackAdminName'))}
          </div>
          {!isCollapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-zinc-900 truncate">
                {user?.name ?? t('layout.fallbackAdminUser')}
              </p>
              <p className="text-xs text-zinc-400 truncate">{user?.email ?? ''}</p>
            </div>
          )}
          <button
            type="button"
            onClick={() => setLogoutDialogOpen(true)}
            title={t('layout.logout')}
            disabled={logoutMutation.isPending}
            className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-600 transition-colors shrink-0"
          >
            <LogOut size={15} />
          </button>
        </div>
      </aside>

      <ConfirmDialog
        open={logoutDialogOpen}
        title={t('layout.logoutConfirmTitle')}
        description={t('layout.logoutConfirmDescription')}
        confirmLabel={t('layout.logout')}
        cancelLabel={t('common.cancel')}
        loading={logoutMutation.isPending}
        onConfirm={() => {
          if (logoutMutation.isPending) return
          void handleLogout()
        }}
        onCancel={() => setLogoutDialogOpen(false)}
      />

      {/* Main content */}
      <div
        className="flex-1 min-h-0 min-w-0 flex flex-col bg-white rounded-xl border border-zinc-200 overflow-hidden"
        style={{ boxShadow: '0 1px 2px -1px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.06)' }}
      >
        <div className="shrink-0 flex items-center justify-end px-4 py-2 border-b border-zinc-100">
          <div className="flex items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-1">
            {DASHBOARD_LANGUAGE_OPTIONS.map((language) => (
              <button
                key={language.value}
                type="button"
                onClick={() => handleLanguageChange(language)}
                className={cn(
                  'rounded px-3 py-1 text-xs font-medium transition-colors',
                  activeLanguage === language.value
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-500 hover:text-zinc-800',
                )}
              >
                {language.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
      </div>
    </div>
  )
}
